/* HUENIT LAB 2.0 — Admin: AI 예제 (개선3, R-0156).
   공식 AI 학습 모델(kfpkg)을 코드 예제와 같은 huenit-lab-code 버킷의
   official/manifest.json(aiModel kind)으로 등록·삭제한다. 앱은 R-0153
   AiExampleCatalog.fetchOfficial(aiModel) 이 소비 — 게시 즉시 예제 탭 AI 섹션 노출.
   kfpkg sanity(repo.validateKfpkg)로 깨진 모델을 업로드 전 차단(카메라 벽돌 예방).
   fake 모드(env 미설정)도 동일 코드로 동작(s3.js fake). */
import { useState, useRef, useEffect, useCallback } from 'react';
import { Icon } from '../../icons/Icon.jsx';
import { Modal, Confirm, MLText, useToast, Loading, ErrorState, EmptyState } from '../../components/index.js';
import { useLocal, LANG_SEED } from '../../data/store.js';
import { mlGet } from '../../lib/ml.js';
import { useSession } from '../../auth/session.jsx';
import { listAiModels, saveAiModel, deleteAiModel, newModelId, modelFileUrl } from './repo.js';
import { downloadObject } from '../../lib/download.js';
import { inspectKfpkg, detectTypeValue } from './kfpkgInspect.js';

const SEED_FLAG = 'adm.fake.aiModelsSeeded';
let seedInFlight = null;   // StrictMode 이중 마운트의 동시 시드 RMW 경쟁 방지(모듈 단위 가드)

/* 모델 종류 — CMS 전용 분류(localStorage, plan §7.2 패턴). 다국어 라벨.
   meta 에 저장되는 것은 value 문자열뿐. ★ 앱(_modelTypeOf)은 값에 yolo/detection 이
   포함되면 탐지(YOLO), 그 외는 분류로 동작 — 새 종류를 추가해도 앱은 안전(그룹만 저 규칙). */
const SEED_MODEL_TYPES = [
  { value: 'classification', label: { ko: '이미지 분류', en: 'Image Classification' } },
  { value: 'object_detection', label: { ko: '객체 탐지 (YOLO)', en: 'Object Detection (YOLO)' } },
  { value: 'face_recognition', label: { ko: '얼굴 인식', en: 'Face Recognition' } },
];

function humanSize(bytes) {
  if (!bytes) return '—';
  const u = ['B', 'KB', 'MB', 'GB']; let n = bytes, i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return (i === 0 ? n : (n < 10 ? n.toFixed(2) : n.toFixed(1))) + ' ' + u[i];
}

/* 데모 시드용 최소 유효 kfpkg(ZIP 매직 + 패딩 ≥ 최소크기) — validateKfpkg 통과용. */
const fakeKfpkg = () => { const b = new Uint8Array(2048); b[0] = 0x50; b[1] = 0x4b; b[2] = 0x03; b[3] = 0x04; return b.buffer; };
const SEED_AI = [
  { displayNameI18n: { ko: '이미지 분류', en: 'Image Classification' }, modelType: 'classification' },
  { displayNameI18n: { ko: 'YOLO 객체 탐지', en: 'YOLO Object Detection' }, modelType: 'object_detection' },
];

/* ============ kfpkg 드롭존 ============ */
function KfpkgDrop({ fileName, onPick, onClear }) {
  const toast = useToast();
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const pick = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.kfpkg')) { toast('.kfpkg 모델 파일만 올릴 수 있습니다.', 'error'); return; }
    onPick(f);
  };
  return (
    <div>
      {fileName ? (
        <div className="dropzone__file">
          <Icon name="ai" size={18} />
          <span className="dropzone__file-name">{fileName}</span>
          <button type="button" className="icon-btn icon-btn--ghost" onClick={onClear}><Icon name="close" size={16} /></button>
        </div>
      ) : (
        <div className={"dropzone" + (over ? " is-over" : "")}
          onClick={() => inputRef.current && inputRef.current.click()}
          onDragOver={e => { e.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={e => { e.preventDefault(); setOver(false); pick(e.dataTransfer.files[0]); }}>
          <div className="dropzone__icon"><Icon name="upload" size={22} /></div>
          <div className="dropzone__t">AI 모델 파일(.kfpkg)을 끌어다 놓기</div>
          <div className="dropzone__d">또는 클릭해서 선택 · K210 학습 모델 패키지</div>
          <input ref={inputRef} type="file" accept=".kfpkg" hidden onChange={e => pick(e.target.files[0])} />
        </div>
      )}
    </div>
  );
}

/* ============ AI 예제 등록/수정 모달 ============ */
function AiModelModal({ draft, setDraft, langs, types, typeLabel, onManageTypes, onSave, onClose, saving, step, isNew }) {
  const toast = useToast();
  const d = draft;
  const set = (patch) => setDraft(prev => ({ ...prev, ...patch }));
  const def = (langs.find(l => l.default) || langs[0]).code;
  // 수정 시 파일은 선택(미교체면 기존 모델 유지). 신규는 필수.
  const canSave = !saving && (d.displayNameI18n[def] || '').trim() && d.modelType && (isNew ? !!d.fileObj : true);

  /* 파일 선택 → kmodel_info.bin 파싱으로 종류 자동 감지(best-effort).
     비어있으면 자동 입력, 이미 선택돼 있고 다르면 경고만(사용자 선택 존중). */
  const pickFile = async (f) => {
    set({ fileObj: f, file: f.name });
    const prevType = d.modelType;
    const info = await inspectKfpkg(await f.arrayBuffer());
    const detected = detectTypeValue(info, f.name);
    if (!detected) return;
    const extra = info && info.classNum != null ? ` · 클래스 ${info.classNum}개` : '';
    if (!prevType) {
      set({ modelType: detected });
      toast(`모델 종류 자동 감지: ${typeLabel(detected)}${extra}`);
    } else if (prevType !== detected) {
      toast(`감지된 종류(${typeLabel(detected)})와 선택(${typeLabel(prevType)})이 다릅니다 — 확인하세요.`, 'error');
    }
  };

  // 현재 값이 목록에 없으면(삭제된 종류·감지값) 잃지 않도록 옵션에 노출.
  const hasCurrent = !d.modelType || types.some(t => t.value === d.modelType);

  return (
    <Modal title={isNew ? 'AI 예제 등록' : 'AI 예제 수정'} sub="K210 학습 모델(.kfpkg)을 공식 예제로 게시합니다." onClose={saving ? () => {} : onClose}
      foot={<>
        <button className="btn btn--ghost" onClick={onClose} disabled={saving}>취소</button>
        <button className="btn btn--primary" disabled={!canSave} onClick={onSave}>
          {saving ? (step || '저장 중…') : (isNew ? '예제 등록' : '저장')}
        </button>
      </>}>
      <div className="field">
        <label className="field__label">모델 파일{isNew && <span className="req">*</span>}</label>
        <KfpkgDrop fileName={d.file}
          onPick={pickFile}
          onClear={() => set({ fileObj: null, file: '' })} />
        <div className="field__hint" style={{ fontSize: 12, color: 'var(--ink-400)', marginTop: 4 }}>
          {isNew
            ? '파일을 올리면 모델 종류를 자동 감지합니다. 업로드 전 ZIP(kfpkg) 무결성도 검사합니다.'
            : '새 파일을 올리면 모델을 교체합니다. 비워두면 기존 모델을 유지하고 표시명·종류만 수정합니다.'}
        </div>
      </div>
      <div className="field">
        <label className="field__label">모델 종류<span className="req">*</span></label>
        <div style={{ display: 'flex', gap: 8 }}>
          <select className="select-el" value={d.modelType} onChange={e => set({ modelType: e.target.value })}>
            <option value="">선택하세요</option>
            {types.map(t => <option key={t.value} value={t.value}>{typeLabel(t.value)}</option>)}
            {!hasCurrent && <option value={d.modelType}>{d.modelType} (목록에 없음)</option>}
          </select>
          <button type="button" className="btn btn--ghost" style={{ flexShrink: 0 }} onClick={onManageTypes} title="모델 종류 추가/삭제">
            <Icon name="settings" size={15} />종류 관리
          </button>
        </div>
      </div>
      <div className="field">
        <label className="field__label">표시명<span className="req">*</span></label>
        <MLText value={d.displayNameI18n} onChange={v => set({ displayNameI18n: v })} langs={langs} placeholder="예제 표시명 (언어별)" />
      </div>
    </Modal>
  );
}

/* ============ 모델 종류 관리 모달 — 추가(다국어 라벨)/삭제 ============ */
function TypeManagerModal({ types, setTypes, langs, usedValues, onClose }) {
  const toast = useToast();
  const def = (langs.find(l => l.default) || langs[0]).code;
  const [label, setLabel] = useState({});
  const [value, setValue] = useState('');
  const [valueTouched, setValueTouched] = useState(false);
  const [del, setDel] = useState(null);   // 삭제 확인 대상

  const slug = (s) => String(s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  // 값(영문 식별자) 자동 제안 — en 라벨 우선, 없으면 기본언어 라벨. 직접 수정하면 유지.
  const onLabelChange = (v) => {
    setLabel(v);
    if (!valueTouched) setValue(slug(v.en || v[def] || ''));
  };
  const add = () => {
    const val = slug(value);
    if (!val) { toast('값(영문 식별자)을 입력하세요.', 'error'); return; }
    if (!(label[def] || '').trim()) { toast('기본 언어 라벨을 입력하세요.', 'error'); return; }
    if (types.some(t => t.value === val)) { toast('이미 있는 종류 값입니다.', 'error'); return; }
    setTypes([...types, { value: val, label }]);
    setLabel({}); setValue(''); setValueTouched(false);
    toast('모델 종류를 추가했습니다');
  };
  const remove = (t) => {
    setTypes(types.filter(x => x.value !== t.value));
    setDel(null);
    toast('모델 종류를 삭제했습니다');
  };

  return (
    <Modal title="모델 종류 관리" sub="AI 예제에 지정할 수 있는 모델 종류를 추가·삭제합니다." onClose={onClose}
      foot={<button className="btn btn--ghost" onClick={onClose}>닫기</button>}>
      <div className="rows" style={{ marginBottom: 16 }}>
        {types.map(t => (
          <div className="row" key={t.value} style={{ padding: '10px 14px' }}>
            <div className="row__main">
              <div className="row__title" style={{ fontSize: 13.5 }}>{mlGet(t.label, def) || t.value}
                {usedValues.has(t.value) && <span className="chip" style={{ background: 'var(--brand-50)', color: 'var(--brand-700)', marginLeft: 6 }}>사용 중</span>}
              </div>
              <div className="row__sub mono">{t.value}</div>
            </div>
            <div className="row__actions">
              <button className="icon-btn icon-btn--danger" onClick={() => setDel(t)} title="삭제"><Icon name="trash" size={15} /></button>
            </div>
          </div>
        ))}
        {types.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--ink-400)', padding: '6px 2px' }}>종류가 없습니다. 아래에서 추가하세요.</div>}
      </div>

      <div className="field">
        <label className="field__label">새 종류 라벨<span className="req">*</span></label>
        <MLText value={label} onChange={onLabelChange} langs={langs} placeholder="예: 손동작 인식" />
      </div>
      <div className="field">
        <label className="field__label">값 (영문 식별자)<span className="req">*</span></label>
        <input className="input mono" value={value} placeholder="예: gesture_recognition"
          onChange={e => { setValue(e.target.value); setValueTouched(true); }} />
        <div className="field__hint">meta 에 저장되는 값입니다. <b>yolo</b> 또는 <b>detection</b> 이 포함되면 앱이 탐지(YOLO) 모델로, 그 외에는 분류 모델로 동작합니다.</div>
      </div>
      <button className="btn btn--soft" onClick={add}><Icon name="plus" size={15} stroke={2.2} />종류 추가</button>

      {del && <Confirm title="이 모델 종류를 삭제할까요?"
        body={<><b>{mlGet(del.label, def) || del.value}</b> 종류를 목록에서 삭제합니다.{usedValues.has(del.value) && <><br /><b>이 종류를 사용 중인 예제가 있습니다</b> — 기존 예제의 값은 유지되고 표시만 원문 값으로 나옵니다.</>}</>}
        confirmLabel="삭제"
        onConfirm={() => remove(del)} onClose={() => setDel(null)} />}
    </Modal>
  );
}

/* ============ AI 예제 관리 ============ */
export function ExamplesAiAdmin() {
  const toast = useToast();
  const { mode, email, getCredentials } = useSession();
  const [langs] = useLocal('adm.langs', LANG_SEED);
  const def = (langs.find(l => l.default) || langs[0]).code;
  const [modelTypes, setModelTypes] = useLocal('adm.aiModelTypes', SEED_MODEL_TYPES);
  const [typeMgr, setTypeMgr] = useState(false);
  const typeLabel = (v) => { const t = modelTypes.find(x => x.value === v); return t ? (mlGet(t.label, def) || v) : (v || '—'); };

  const [items, setItems] = useState(null);   // null=로딩, []=빈, [..]=목록
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);   // null=닫힘, 'new'=등록, <id>=수정
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState('');
  const [del, setDel] = useState(null);
  const [delBusy, setDelBusy] = useState(false);

  const owner = () => ({ email: email || '', defaultLang: def });

  const seedFakeOnce = useCallback(async () => {
    try { if (localStorage.getItem(SEED_FLAG)) return; } catch (e) { return; }
    if (seedInFlight) return seedInFlight;
    seedInFlight = (async () => {
      for (const m of SEED_AI) {
        await saveAiModel(getCredentials, { model: { id: newModelId(), ...m }, fileBuffer: fakeKfpkg(), owner: owner() });
      }
      try { localStorage.setItem(SEED_FLAG, '1'); } catch (e) { /* ignore */ }
    })();
    try { await seedInFlight; } finally { seedInFlight = null; }
  }, [getCredentials, email, def]);

  const load = useCallback(async () => {
    setError(null); setItems(null);
    try {
      if (mode === 'fake') await seedFakeOnce();
      setItems(await listAiModels());
    } catch (e) {
      setError(e && e.message ? e.message : String(e));
      setItems([]);
    }
  }, [mode, seedFakeOnce]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setDraft({ id: newModelId(), file: '', fileObj: null, modelType: '', displayNameI18n: {} }); setEditing('new'); };
  const openEdit = (m) => {
    setDraft({ id: m.id, file: '', fileObj: null, modelType: m.modelType || '', displayNameI18n: { ...m.displayNameI18n } });
    setEditing(m.id);
  };
  const closeModal = () => { if (!saving) { setEditing(null); setDraft(null); } };

  const save = async () => {
    const isNew = editing === 'new';
    setSaving(true); setStep('');
    try {
      const fileBuffer = draft.fileObj ? await draft.fileObj.arrayBuffer() : null;
      // 종류 다국어 라벨(adm.aiModelTypes)을 meta.extras.model_type_i18n 으로 동반 발행(26.07.04 §4).
      const typeEntry = modelTypes.find(t => t.value === draft.modelType);
      await saveAiModel(getCredentials, { model: { ...draft, modelTypeI18n: typeEntry ? typeEntry.label : null }, fileBuffer, owner: owner(), onStep: setStep });
      toast(isNew ? 'AI 예제를 등록했습니다' : 'AI 예제를 수정했습니다');
      setEditing(null); setDraft(null);
      await load();
    } catch (e) {
      toast((isNew ? '등록' : '수정') + ' 실패: ' + (e && e.message ? e.message : e), 'error');
    } finally {
      setSaving(false); setStep('');
    }
  };

  const remove = async () => {
    setDelBusy(true);
    try {
      await deleteAiModel(getCredentials, del.id);
      toast('AI 예제를 삭제했습니다');
      setDel(null);
      await load();
    } catch (e) {
      toast('삭제 실패: ' + (e && e.message ? e.message : e), 'error');
    } finally {
      setDelBusy(false);
    }
  };

  const download = async (m) => {
    if (mode === 'fake') { toast('데모(오프라인) 모드에서는 실제 파일 다운로드를 지원하지 않습니다.'); return; }
    const base = (mlGet(m.displayNameI18n, def) || m.displayName || m.id).replace(/[\\/:*?"<>|]+/g, '_');
    try {
      await downloadObject(modelFileUrl(m.id), base + '.kfpkg');
    } catch (e) {
      toast('다운로드 실패: ' + (e && e.message ? e.message : e), 'error');
    }
  };

  const list = items || [];

  return (
    <div>
      <div className="toolbar">
        <div style={{ flex: 1, fontSize: 13, color: 'var(--ink-400)' }}>
          공식 AI 학습 모델(.kfpkg)을 예제로 게시합니다 — 앱 예제 탭 AI 섹션에 즉시 노출됩니다.
        </div>
        <button className="btn btn--ghost" onClick={() => setTypeMgr(true)}><Icon name="settings" size={15} />모델 종류 관리</button>
        <button className="btn btn--primary" onClick={openNew}><Icon name="plus" size={15} stroke={2.2} />AI 예제 등록</button>
      </div>

      {items === null ? (
        <Loading title="불러오는 중…" sub="AI 예제 목록을 가져오고 있습니다." />
      ) : error ? (
        <ErrorState error={error} title="목록을 불러오지 못했습니다" onRetry={load} />
      ) : list.length === 0 ? (
        <EmptyState icon="ai" title="등록된 AI 예제가 없습니다" sub="우측 상단 [AI 예제 등록]으로 첫 모델을 게시해보세요." />
      ) : (
        <div className="ex-grid">
          {list.map(m => (
            <div key={m.id} className="ex-card">
              <div className="ex-card__top">
                <span className="ex-card__kind ex-card__kind--block"><Icon name="ai" size={13} />AI 모델</span>
                <span className="ex-card__cat"><span className="dot" style={{ background: '#d6488a' }}></span>{typeLabel(m.modelType)}</span>
              </div>
              <div className="ex-card__body">
                <div className="ex-card__title">{mlGet(m.displayNameI18n, def) || m.displayName || '(이름 없음)'}</div>
                <div className="ex-card__file"><Icon name="storage" size={14} />{humanSize(m.sizeBytes)}{m.sha256 ? ' · sha256 ✓' : ''}</div>
              </div>
              <div className="ex-card__foot">
                <div className="ex-card__langs">
                  {langs.map(l => <span key={l.code} className={"lang-pip" + ((m.displayNameI18n[l.code] || '').trim() ? " is-set" : "")}>{l.code}</span>)}
                </div>
                <button className="icon-btn" onClick={() => download(m)} title="모델 다운로드"><Icon name="download" size={15} /></button>
                <button className="icon-btn" onClick={() => openEdit(m)} title="수정"><Icon name="file" size={15} /></button>
                <button className="icon-btn icon-btn--danger" onClick={() => setDel(m)} title="삭제"><Icon name="trash" size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && draft && (
        <AiModelModal draft={draft} setDraft={setDraft} langs={langs} saving={saving} step={step}
          types={modelTypes} typeLabel={typeLabel} onManageTypes={() => setTypeMgr(true)}
          isNew={editing === 'new'} onSave={save} onClose={closeModal} />
      )}
      {typeMgr && (
        <TypeManagerModal types={modelTypes} setTypes={setModelTypes} langs={langs}
          usedValues={new Set(list.map(m => m.modelType).filter(Boolean))}
          onClose={() => setTypeMgr(false)} />
      )}
      {del && <Confirm title="AI 예제를 삭제할까요?"
        body={<><b>{mlGet(del.displayNameI18n, def) || del.displayName}</b> 모델을 영구 삭제합니다. 되돌릴 수 없습니다.</>}
        confirmLabel={delBusy ? '삭제 중…' : '삭제'}
        onConfirm={delBusy ? () => {} : remove} onClose={delBusy ? () => {} : () => setDel(null)} />}
    </div>
  );
}

export default ExamplesAiAdmin;
